import { h, createContext } from 'preact';
import { useCallback, useContext, useEffect, useState } from 'preact/hooks';
import { getCookie, setCookie, getStoreStatus } from 'utils/helpers';
import { formatLocations } from 'utils/storefront/format-resource';

const Context = createContext();

export const useLocations = () => useContext(Context);

/**
 * Provides access to all location-related data and functionality.
 * Because this isn't a SPA, we use localStorage and sessionStorage to
 * imitate the singleton functionality of React contex providers.
 */
export const LocationsContext = ({
  children,
  currentVariant = null,
  currentProduct = null,
  popularLocationsIds,
  customerId,
  email,
  locationId,
  disabledGeoMsg,
}) => {
  const [userCurrentLocation, setUserCurrentLocation] = useState(null);
  const [allLocations, setAllLocations] = useState(
    sessionStorage.getItem('allLocations')
      ? formatLocations(JSON.parse(sessionStorage.getItem('allLocations')))
      : {},
  );
  const [userSavedStoreId, setUserSavedStoreId] = useState(
    (email && locationId !== 'null' && locationId) ||
      localStorage.getItem('userSavedStoreId'),
  );
  const [noResults, setNoResults] = useState(false);
  const [userSavedStore, setUserSavedStore] = useState('');
  const [locationServicesError, setLocationServicesError] = useState(null);
  const [results, setResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [stockData, setStockData] = useState(null);

  const popularLocations = allLocations
    ? popularLocationsIds.map((id) => allLocations[id]).filter((i) => i)
    : [];

  /**
   * Context initialization
   */
  useEffect(() => {
    const savedLocation = JSON.parse(
      localStorage.getItem('userCurrentLocation'),
    );
    if (savedLocation) {
      if (savedLocation.type === 'geo' || getCookie('store_search')) {
        setUserCurrentLocation(savedLocation);
      } else if (getCookie('geo_enabled')) {
        useMyLocation();
      } else {
        setUserCurrentLocation(null);
      }
    }

    // If location data isn't cached in sessionStorage fetch it from the API
    if (sessionStorage.getItem('allLocations') === null) {
      const technicalDataEl = document.querySelector('[data-technical]');
      const technicalData = technicalDataEl
        ? JSON.parse(technicalDataEl.textContent)
        : {};
      const apiHost = technicalData.discountsApi;
      const pathSuffix = window.STOREFRONT === 'LC' ? 'lc' : 'ml';
      fetch(`${apiHost}/locations/${pathSuffix}`)
        // TODO: error handling
        .then((res) => res.json())
        .then((data) => {
          setAllLocations(formatLocations(data.locations));
          // Store in sessionStorage
          sessionStorage.setItem(
            'allLocations',
            JSON.stringify(data.locations),
          );
          document.dispatchEvent(new CustomEvent('storeLocationsLoaded'));
        });
    }
  }, []);

  const persistUserSavedStore = useCallback(() => {
    const newSavedStore = localStorage.getItem('userSavedStoreId');
    if (newSavedStore && allLocations && allLocations[newSavedStore]) {
      setUserSavedStore({
        ...allLocations[newSavedStore],
        status: getStoreStatus(allLocations[newSavedStore]),
      });
    } else {
      setUserSavedStore(null);
    }
  }, [allLocations]);

  useEffect(() => {
    document.addEventListener('savedStoreChanged', persistUserSavedStore);
    return () => {
      document.removeEventListener('savedStoreChanged', persistUserSavedStore);
    };
  }, [persistUserSavedStore]);

  /**
   * When the current location is updated, also update the results
   */
  useEffect(() => {
    setLoadingResults(true);
    localStorage.setItem(
      'userCurrentLocation',
      JSON.stringify(userCurrentLocation),
    );
    if (userCurrentLocation) {
      if (allLocations && window.MAPS_LOADED) {
        const sortedLocations = getLocationsByDistance(userCurrentLocation);
        setResults(sortedLocations);
      } else {
        setResults([]);
      }
    } else {
      setResults([]);
    }
    setLoadingResults(false);
  }, [userCurrentLocation, allLocations, locationServicesError]);

  /**
   * When the userSavedStoreId is changed, fire an event to let all contexts know
   * they should update the value from localStorage
   */
  useEffect(() => {
    if (email && userSavedStoreId !== locationId) {
      const technicalDataEl = document.querySelector('[data-technical]');
      const technicalData = technicalDataEl
        ? JSON.parse(technicalDataEl.textContent)
        : {};
      const apiHost = technicalData.discountsApi;
      const pathSuffix = window.STOREFRONT === 'LC' ? 'lc' : 'ml';
      fetch(`${apiHost}/api/customers/store/update/${pathSuffix}`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          customer_id: customerId,
          email,
          store_id: userSavedStoreId,
        }),
      }).then((response) => response.json());
    }
    localStorage.setItem('userSavedStoreId', userSavedStoreId ?? '');
    document.dispatchEvent(new CustomEvent('savedStoreChanged'));
  }, [userSavedStoreId]);

  /**
   * When the results is changed, request stock data if product is specified
   */
  useEffect(() => {
    if (currentProduct && currentVariant?.id) {
      const storeIds = (results.length ? results : popularLocations).map(
        (item) => item.id,
      );
      if (userSavedStore) {
        storeIds.push(userSavedStore.id);
      }
      getStockData(
        storeIds.filter((id, i) => storeIds.indexOf(id) === i),
        currentProduct,
        currentVariant,
      );
    }
  }, [currentProduct, currentVariant]);

  /**
   * Get stock data for product by stores
   * @param {string[]} storeIds
   * @param {Object} product
   * @param {?Object} variant
   * TODO refactor when BE endpoint (LC-214) will be ready. If possible retrieve stockData for all locations, storeIds dependency should be removed
   */
  const getStockData = (storeIds, product, variant) => {
    if (!variant || !variant.sku) {
      return null;
    }
    const technicalDataEl = document.querySelector('[data-technical]');
    const technicalData = technicalDataEl
      ? JSON.parse(technicalDataEl.textContent)
      : {};
    const apiHost = technicalData.discountsApi;
    const pathSuffix = window.STOREFRONT === 'LC' ? 'lc' : 'ml';
    const variantId = variant.id.replace('gid://shopify/ProductVariant/', '');
    fetch(`${apiHost}/api/shopify/variant/store-inventory/${pathSuffix}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        variantId,
        locationId: technicalData.inventoryLocationId,
      }),
    })
      .then((response) => response.json())
      .then((response) => {
        const result = response?.result;
        setStockData(result);
      });
    return null;
  };

  /**
   *  Attempt to use HTML5 geolocation to find nearest stores
   */
  function useMyLocation(triggerType = '') {
    setLoadingResults(true);
    setLocationServicesError(null);
    // Try HTML5 geolocation.
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Clear the autocomplete input for clearer UX
          if (document.getElementById('pac-input')) {
            document.getElementById('pac-input').value = '';
          }
          setUserCurrentLocation({
            title: 'You',
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            type: 'geo',
          });
          localStorage.setItem(
            'tmwUserGeoLocation',
            JSON.stringify({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            }),
          );
          setCookie('geo_enabled', 1, 30);
        },
        (error) => {
          setCookie('geo_enabled', 1, 0);
          setCookie('geo_disabled', 1, 30);
          // eslint-disable-next-line no-console
          console.error('Error calling getCurrentPosition():', error);
          if (triggerType === 'manual') {
            // eslint-disable-next-line no-alert
            alert(disabledGeoMsg);
          }
          setUserCurrentLocation(null);
          setLoadingResults(false);
        },
      );
    } else {
      // eslint-disable-next-line no-console
      console.warn('Location services unavailable');
      setCookie('geo_enabled', 1, 0);
      setCookie('geo_disabled', 1, 30);
      if (triggerType === 'manual') {
        // eslint-disable-next-line no-alert
        alert(disabledGeoMsg);
      }
      setUserCurrentLocation(null);
      setLoadingResults(false);
    }
  }

  const getLocationsByDistance = (geo) => {
    const validLocations = Object.values(allLocations).filter(
      (l) =>
        (l.address.latitude || l.lat?.value) &&
        (l.address.longitude || l.lng?.value),
    );
    const locationsWithDistance = validLocations.map((l) => {
      const metersAway = google.maps.geometry.spherical.computeDistanceBetween(
        {
          lat: geo.lat,
          lng: geo.lng,
        },
        {
          lat: l.address.latitude || parseFloat(l.lat.value),
          lng: l.address.longitude || parseFloat(l.lng.value),
        },
      );
      let kmAway = null;
      const userGeoLocation = JSON.parse(
        localStorage.getItem('tmwUserGeoLocation'),
      );
      if (userGeoLocation) {
        kmAway = (
          google.maps.geometry.spherical.computeDistanceBetween(
            {
              lat: userGeoLocation.lat,
              lng: userGeoLocation.lng,
            },
            {
              lat: l.address.latitude || parseFloat(l.lat.value),
              lng: l.address.longitude || parseFloat(l.lng.value),
            },
          ) / 1000
        ).toFixed(1);
      }
      return {
        ...l,
        metersAway,
        kmAway,
      };
    });
    return locationsWithDistance.sort((a, b) => a.metersAway - b.metersAway);
  };

  return (
    <Context.Provider
      value={{
        allLocations,
        results,
        loadingResults,
        setLoadingResults,
        userSavedStore,
        userSavedStoreId,
        setUserSavedStoreId,
        userCurrentLocation,
        setUserCurrentLocation,
        locationServicesError,
        setLocationServicesError,
        stockData,
        popularLocations,
        noResults,
        setNoResults,
        currentVariant,
        currentProduct,
        useMyLocation,
        getLocationsByDistance,
      }}
    >
      {children}
    </Context.Provider>
  );
};
